/**
 * Users, roles and scopes (Functional Specifications 4.3, 4.4 and 13.3; FR-TRV-11, FR-TRV-15,
 * FR-TRV-16). Pure rules shared by the API and the web application.
 */
import { z } from 'zod';
import { localeSchema, roleScopeKindSchema } from './enums.js';
const uuid = z.string().uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Roles a customer administrator may grant, and the scopes each one accepts. */
export const ROLE_SCOPES = {
    TENANT_ADMIN: ['TENANT'],
    GROUP_ANALYST: ['TENANT'],
    FINANCE: ['TENANT', 'COMPANY', 'SITE'],
    SITE_ADMIN: ['COMPANY', 'SITE'],
    SITE_MANAGER: ['COMPANY', 'SITE'],
    SITE_OPERATOR: ['COMPANY', 'SITE'],
    SITE_READER: ['COMPANY', 'SITE'],
};
/** In the order of the rights matrix, from the widest to the narrowest. */
export const ASSIGNABLE_ROLES = [
    'TENANT_ADMIN',
    'GROUP_ANALYST',
    'SITE_ADMIN',
    'SITE_MANAGER',
    'SITE_OPERATOR',
    'SITE_READER',
    'FINANCE',
];
/** Roles a site administrator may grant on their own sites ("site users only", FS 4.3). */
export const SITE_ADMIN_GRANTABLE_ROLES = [
    'SITE_MANAGER',
    'SITE_OPERATOR',
    'SITE_READER',
];
export const roleAssignmentSchema = z
    .object({
    role: z.enum(ASSIGNABLE_ROLES),
    scopeKind: roleScopeKindSchema,
    scopeId: uuid,
    /** Time-boxed access, for example an auditor (FS 4.4); the role stops applying after this day. */
    expiresOn: isoDate.nullish(),
})
    .superRefine((a, ctx) => {
    if (!ROLE_SCOPES[a.role]?.includes(a.scopeKind)) {
        ctx.addIssue({
            code: 'custom',
            path: ['scopeKind'],
            message: `The role ${a.role} cannot be granted on a ${a.scopeKind.toLowerCase()} scope`,
        });
    }
});
const assignmentList = z.array(roleAssignmentSchema).max(100);
function noDuplicates(list, ctx) {
    const seen = new Set();
    list.forEach((a, i) => {
        const key = assignmentKey(a);
        if (seen.has(key)) {
            ctx.addIssue({
                code: 'custom',
                path: [i],
                message: 'The same role is granted twice on the same scope',
            });
        }
        seen.add(key);
    });
}
export const userInviteSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(200),
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    locale: localeSchema.default('fr'),
    assignments: assignmentList.min(1).superRefine(noDuplicates),
});
export const userUpdateSchema = z.object({
    firstName: z.string().trim().min(1).max(80).optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    locale: localeSchema.optional(),
    assignments: assignmentList.superRefine(noDuplicates).optional(),
});
export const userDeactivateSchema = z.object({
    reason: z.string().trim().min(5).max(500),
});
export const userPreferencesSchema = z.object({
    locale: localeSchema.optional(),
    notifications: z
        .object({
        /** Alerts by e-mail as they happen (expiry, refusal, overdue return). */
        email: z.boolean(),
        /** The daily digest of open alerts (FR-TRV-03). */
        dailyDigest: z.boolean(),
    })
        .partial()
        .optional(),
});
export const DEFAULT_NOTIFICATION_PREFERENCES = { email: true, dailyDigest: true };
export const auditQuerySchema = z.object({
    from: isoDate.optional(),
    to: isoDate.optional(),
    userId: uuid.optional(),
    siteId: uuid.optional(),
    entityType: z.string().max(60).optional(),
    entityId: uuid.optional(),
    /** Prefix or full action, e.g. "cap" or "cap.renew". */
    action: z.string().max(80).optional(),
    /** Paging: entries strictly older than this entry id (uuid v7, time ordered). */
    before: uuid.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export function assignmentKey(a) {
    return `${a.role}|${a.scopeKind}|${a.scopeId}`;
}
/** Whether an assignment still applies on a day (expiry is inclusive of its last day). */
export function assignmentActive(a, today) {
    return !a.expiresOn || a.expiresOn >= today;
}
/**
 * The assignments added and removed between two lists, compared on role and scope; a change of
 * expiry counts as a removal and an addition, so both sides are checked against the actor's rights.
 */
export function diffAssignments(before, after) {
    const full = (a) => `${assignmentKey(a)}|${a.expiresOn ?? ''}`;
    const beforeKeys = new Set(before.map(full));
    const afterKeys = new Set(after.map(full));
    return {
        added: after.filter((a) => !beforeKeys.has(full(a))),
        removed: before.filter((a) => !afterKeys.has(full(a))),
    };
}
/**
 * Two-step verification (ADR-0006): the people who administer people or settings sign in with a
 * second factor (an authenticator application, a security key, or a passkey, which is both). The
 * portal gives them Keycloak's realm role MFA_ROLE; Keycloak's sign-in flow then asks for the
 * second factor, and has them set one up when they have none.
 */
export const MFA_ROLE = 'mfa-required';
/** Roles whose holders sign in with two-step verification, besides platform operators. */
export const TWO_STEP_ROLES = ['TENANT_ADMIN', 'SITE_ADMIN'];
export function requiresTwoStep(roles, platformOperator) {
    return platformOperator || roles.some((role) => TWO_STEP_ROLES.includes(role));
}
//# sourceMappingURL=administration.js.map